import { c as _c } from "react/compiler-runtime";
import { Checkbox, Link, MenuItem, Select, Skeleton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useSalesforceBrand } from '@/modules/salesforce/brand';
import { useSalesforceConnectionLazyQuery } from '@/pages/admin/AdminIntegrationsPage/graphql/SalesforceIntegration.generated';
import type { FormDraftInput } from '../AdminFormsPage.model';
import { buildImportPreview, createSalesforceDraft, type ImportPreview, type SalesforceDescription, type SalesforceLayout, type SalesforceObjectSummary, uniqueHouseholdLookup, usesWealthHubDestination } from './formImport';
import { mapDescription, mapLayouts, mapObjects } from './metadata';
import { useSalesforceFormObjectsLazyQuery, useSalesforceLayoutsLazyQuery, useSalesforceObjectDescriptionLazyQuery } from './SalesforceMetadata.generated';
type State = {
  kind: 'loading';
} | {
  kind: 'unavailable';
  message: string;
} | {
  kind: 'error';
  message: string;
} | {
  kind: 'objects';
  objects: SalesforceObjectSummary[];
} | {
  kind: 'layouts';
  object: SalesforceObjectSummary;
  description: SalesforceDescription;
  layouts: SalesforceLayout[];
} | {
  kind: 'preview';
  description: SalesforceDescription;
  preview: ImportPreview;
  layoutWarning?: string;
};
const SALESFORCE_FORM_IMPORT_MAX_HEIGHT = 'calc(100% - 114px)';
interface Props {
  isCreating: boolean;
  onClose: () => void;
  onCreate: (draft: FormDraftInput) => Promise<void>;
}
export function SalesforceFormImportModal({
  isCreating,
  onClose,
  onCreate
}: Props) {
  const brand = useSalesforceBrand();
  const [getConnection] = useSalesforceConnectionLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const [getObjects] = useSalesforceFormObjectsLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const [getDescription] = useSalesforceObjectDescriptionLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const [getLayouts] = useSalesforceLayoutsLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const [state, setState] = useState<State>({
    kind: 'loading'
  });
  const [query, setQuery] = useState('');
  const [pendingObject, setPendingObject] = useState<string>();
  const [householdObject, setHouseholdObject] = useState<string>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sectionByField, setSectionByField] = useState<Record<string, string>>({});
  const request = useRef(0);
  const loadObjects = useCallback(async () => {
    const current = ++request.current;
    setState({
      kind: 'loading'
    });
    setHouseholdObject(undefined);
    try {
      const connectionResult = await getConnection();
      if (connectionResult.data?.salesforceConnection?.connected !== true) {
        setState({
          kind: 'unavailable',
          message: `Connect ${brand.name} in integration settings before creating a form.`
        });
        return;
      }
      setHouseholdObject(connectionResult.data.salesforceConnection.object ?? undefined);
      const objectsResult = await getObjects();
      if (!objectsResult.data) throw new Error('Salesforce objects returned no data');
      const objects = mapObjects(objectsResult.data.salesforceObjects);
      if (request.current === current) setState({
        kind: 'objects',
        objects
      });
    } catch {
      if (request.current === current) setState({
        kind: 'error',
        message: `${brand.name} objects could not be loaded.`
      });
    }
  }, [brand, getConnection, getObjects]);
  useEffect(() => {
    loadObjects().catch(() => undefined);
    return () => {
      request.current += 1;
    };
  }, [loadObjects]);
  const showPreview = (description: SalesforceDescription, layout?: SalesforceLayout, layoutWarning?: string) => {
    const preview = buildImportPreview(description, layout, householdObject);
    setSelected(new Set(preview.fields.map(field => field.apiName)));
    setSectionByField(Object.fromEntries(preview.sections.flatMap(section => section.fieldApiNames.map(name => [name, section.id]))));
    setState({
      kind: 'preview',
      description,
      preview,
      layoutWarning
    });
  };
  const selectObject = async (object: SalesforceObjectSummary) => {
    const current_0 = ++request.current;
    setPendingObject(object.apiName);
    try {
      const descriptionResult = await getDescription({
        variables: {
          object: object.apiName
        }
      });
      if (!descriptionResult.data) throw new Error('Salesforce object description returned no data');
      const description_0 = mapDescription(descriptionResult.data.salesforceObjectDescription);
      if (request.current !== current_0) return;
      try {
        const layoutsResult = await getLayouts({
          variables: {
            object: object.apiName
          }
        });
        if (!layoutsResult.data) throw new Error('Salesforce layouts returned no data');
        const layouts = mapLayouts(layoutsResult.data.salesforceLayouts);
        if (request.current !== current_0) return;
        if (layouts.length > 1) {
          setState({
            kind: 'layouts',
            object,
            description: description_0,
            layouts
          });
        } else if (layouts.length === 1) {
          showPreview(description_0, layouts[0]);
        } else {
          showPreview(description_0, undefined, `${brand.name} returned no editable layout. All supported fields are shown in one section.`);
        }
      } catch {
        if (request.current === current_0) showPreview(description_0, undefined, `${brand.name} layouts could not be read. All supported fields are shown in one section.`);
      }
    } catch {
      if (request.current === current_0) setState({
        kind: 'error',
        message: `${object.label} metadata could not be read.`
      });
    } finally {
      if (request.current === current_0) setPendingObject(undefined);
    }
  };
  const previewState = state.kind === 'preview' ? state : undefined;
  const missingRequired = previewState?.preview.fields.filter(field_0 => field_0.required && !selected.has(field_0.apiName)) ?? [];
  const householdLookup = previewState ? uniqueHouseholdLookup(previewState.description, householdObject) : undefined;
  const hierarchical = previewState && usesWealthHubDestination(previewState.description, householdObject);
  const canCreate = Boolean(previewState && selected.size > 0 && missingRequired.length === 0 && previewState.preview.blockingRequiredFields.length === 0 && (householdLookup || hierarchical));
  return <DialogModal isOpen heading={`Create a form from ${brand.name}`} subheading={`Choose a ${brand.name} object and the fields to add as questions.`} onClose={onClose} dialogPaperSx={{
    maxHeight: SALESFORCE_FORM_IMPORT_MAX_HEIGHT
  }} actions={<Stack direction="row" gap={1} justifyContent="flex-end">
          {previewState && <Button size="sm" variant="secondary" disabled={isCreating} onClick={() => loadObjects().catch(() => undefined)}>
              Choose another object
            </Button>}
          <Button size="sm" variant="primary" loading={isCreating} disabled={!canCreate || isCreating} onClick={() => {
      if (previewState) onCreate(createSalesforceDraft(previewState.description, previewState.preview, selected, sectionByField, householdObject)).catch(() => undefined);
    }}>
            Create draft form
          </Button>
        </Stack>}>
      {state.kind === 'loading' && <Stack role="status" spacing={1}>
          <Typography variant="body-s">
            Loading {brand.name} objects…
          </Typography>
          <Skeleton height={180} />
        </Stack>}
      {state.kind === 'unavailable' && <Callout severity="warning">
          <span role="status">
            {state.message}{' '}
            <Link href="/admin/integrations">Open integration settings</Link>
          </span>
        </Callout>}
      {state.kind === 'error' && <Stack spacing={1}>
          <Callout severity="error">
            <span role="alert">{state.message}</span>
          </Callout>
          <Button size="sm" variant="secondary" onClick={() => loadObjects().catch(() => undefined)}>
            Try again
          </Button>
        </Stack>}
      {state.kind === 'objects' && <ObjectPicker objects={state.objects} query={query} pendingObject={pendingObject} onQuery={setQuery} onSelect={object_0 => selectObject(object_0).catch(() => undefined)} />}
      {state.kind === 'layouts' && <Stack spacing={1}>
          <Typography variant="title-xs">Choose a page layout</Typography>
          <Typography variant="body-xs">
            The layout sets the initial section and question order.
          </Typography>
          {state.layouts.map(layout_0 => <Button key={layout_0.id} size="sm" variant="secondary" onClick={() => showPreview(state.description, layout_0)}>
              {layout_0.label}
            </Button>)}
        </Stack>}
      {previewState && <Stack spacing={1.5}>
          {hierarchical && <Callout severity="info-low">
              Choose the Household or Family and optional Trust legal entity at
              export time. The linked Household does not change.
            </Callout>}
          {!householdLookup && !hierarchical && <Callout severity="error">
              <span role="alert">
                {brand.name} must provide exactly one writable lookup to the
                configured household object before this export form can be
                created.
              </span>
            </Callout>}
          <Preview state={previewState} selected={selected} sectionByField={sectionByField} missingRequired={missingRequired.map(field_1 => field_1.label)} onSelect={(name_0, checked) => setSelected(current_1 => {
        const next = new Set(current_1);
        if (checked) next.add(name_0);else next.delete(name_0);
        return next;
      })} onMove={(name_1, section_0) => setSectionByField(current_2 => ({
        ...current_2,
        [name_1]: section_0
      }))} />
        </Stack>}
    </DialogModal>;
}
function ObjectPicker(t0) {
  const $ = _c(25);
  const {
    objects,
    query,
    pendingObject,
    onQuery,
    onSelect
  } = t0;
  const brand = useSalesforceBrand();
  let t1;
  if ($[0] !== objects || $[1] !== query) {
    const needle = query.trim().toLowerCase();
    t1 = objects.filter(object => !needle || object.label.toLowerCase().includes(needle) || object.apiName.toLowerCase().includes(needle)).sort(_temp);
    $[0] = objects;
    $[1] = query;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const matches = t1;
  const t2 = `Search ${brand.name} objects`;
  let t3;
  if ($[3] !== onQuery) {
    t3 = event => onQuery(event.target.value);
    $[3] = onQuery;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== query || $[6] !== t2 || $[7] !== t3) {
    t4 = <TextInput autoFocus={true} label={t2} value={query} onChange={t3} />;
    $[5] = query;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = matches.length === 1 ? "" : "s";
  let t6;
  if ($[9] !== matches.length || $[10] !== t5) {
    t6 = <Typography role="status" variant="body-xs">{matches.length} eligible object{t5}</Typography>;
    $[9] = matches.length;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== matches || $[13] !== onSelect || $[14] !== pendingObject) {
    let t8;
    if ($[16] !== onSelect || $[17] !== pendingObject) {
      t8 = object_0 => <Button key={object_0.apiName} size="sm" variant="secondary" loading={pendingObject === object_0.apiName} onClick={() => onSelect(object_0)}>{object_0.label} ({object_0.apiName})</Button>;
      $[16] = onSelect;
      $[17] = pendingObject;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = matches.slice(0, 50).map(t8);
    $[12] = matches;
    $[13] = onSelect;
    $[14] = pendingObject;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <Stack spacing={0.5} maxHeight={320} overflow="auto">{t7}</Stack>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t4 || $[22] !== t6 || $[23] !== t8) {
    t9 = <Stack spacing={1}>{t4}{t6}{t8}</Stack>;
    $[21] = t4;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function _temp(a, b) {
  return Number(b.custom) - Number(a.custom) || a.label.localeCompare(b.label);
}
function Preview(t0) {
  const $ = _c(32);
  const {
    state,
    selected,
    sectionByField,
    missingRequired,
    onSelect,
    onMove
  } = t0;
  const brand = useSalesforceBrand();
  const {
    preview,
    description
  } = state;
  let t1;
  if ($[0] !== description.apiName || $[1] !== description.label) {
    t1 = <Typography variant="title-xs">{description.label} ({description.apiName})</Typography>;
    $[0] = description.apiName;
    $[1] = description.label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== state.layoutWarning) {
    t2 = state.layoutWarning && <Callout severity="warning"><span role="status">{state.layoutWarning}</span></Callout>;
    $[3] = state.layoutWarning;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== brand || $[6] !== preview.blockingRequiredFields) {
    t3 = preview.blockingRequiredFields.length > 0 && <Callout severity="error"><span role="alert">{brand.name} requires unsupported fields:{" "}{preview.blockingRequiredFields.map(_temp2).join(", ")}. Change those fields in {brand.name} before creating this form.</span></Callout>;
    $[5] = brand;
    $[6] = preview.blockingRequiredFields;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== missingRequired) {
    t4 = missingRequired.length > 0 && <Callout severity="error"><span role="alert">Select required fields: {missingRequired.join(", ")}.</span></Callout>;
    $[8] = missingRequired;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onMove || $[11] !== onSelect || $[12] !== preview.fields || $[13] !== preview.sections || $[14] !== sectionByField || $[15] !== selected) {
    let t6;
    if ($[17] !== onMove || $[18] !== onSelect || $[19] !== preview.sections || $[20] !== sectionByField || $[21] !== selected) {
      t6 = field_0 => <Stack key={field_0.apiName} direction={{
        xs: "column",
        sm: "row"
      }} alignItems={{
        sm: "center"
      }} gap={1}><Stack direction="row" alignItems="center" flex={1}><Checkbox checked={selected.has(field_0.apiName)} inputProps={{
            "aria-label": `Select ${field_0.label}`
          }} onChange={event => onSelect(field_0.apiName, event.target.checked)} /><Stack flex={1}><Typography variant="body-s">{field_0.label}{field_0.required ? " (required)" : ""}</Typography><Typography variant="body-xs">{field_0.apiName}</Typography></Stack></Stack><Select size="small" value={sectionByField[field_0.apiName] ?? ""} inputProps={{
          "aria-label": `Section for ${field_0.label}`
        }} onChange={event_0 => onMove(field_0.apiName, event_0.target.value)}>{preview.sections.map(_temp3)}</Select></Stack>;
      $[17] = onMove;
      $[18] = onSelect;
      $[19] = preview.sections;
      $[20] = sectionByField;
      $[21] = selected;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    t5 = preview.fields.map(t6);
    $[10] = onMove;
    $[11] = onSelect;
    $[12] = preview.fields;
    $[13] = preview.sections;
    $[14] = sectionByField;
    $[15] = selected;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[23] !== preview.fields.length) {
    t6 = preview.fields.length === 0 && <Callout severity="warning"><span role="status">This object has no supported editable fields.</span></Callout>;
    $[23] = preview.fields.length;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== t1 || $[26] !== t2 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6) {
    t7 = <Stack spacing={1.5}>{t1}{t2}{t3}{t4}{t5}{t6}</Stack>;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
}
function _temp3(section) {
  return <MenuItem key={section.id} value={section.id}>{section.label}</MenuItem>;
}
function _temp2(field) {
  return field.label;
}